import type { Artifact, Asset, AssetVisibility, Resource } from '@deskworks/shared-types';
import { Copy, Download, ExternalLink, FileText, Globe, Lock } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { api, errorMessage } from '@/lib/api';
import { formatBytes } from '@/lib/format';
import { t, tc } from '@/lib/i18n';
import { useToast } from '@/lib/toast';
import { cn } from '@/lib/utils';

/** A stored file as the preview needs it (docs/features/assets.md). */
export interface PreviewFile {
  assetId: string;
  filename: string;
  mimeType: string;
  size: number;
  visibility?: AssetVisibility;
  publicUrl?: string | null;
  altText?: string | null;
  /** Changes when the file's content does (a resource uploaded again keeps its asset). */
  version?: string;
}

export function artifactFile(a: Artifact): PreviewFile {
  return {
    // Artifacts saved before assets existed carry their asset under the same id.
    assetId: a.assetId ?? a.id,
    filename: a.filename,
    mimeType: a.mimeType,
    size: a.size,
    visibility: a.visibility,
    publicUrl: a.publicUrl,
    altText: typeof a.metadata?.altText === 'string' ? a.metadata.altText : null,
  };
}

export function assetFile(a: Asset): PreviewFile {
  return {
    assetId: a.id,
    filename: a.filename,
    mimeType: a.mimeType,
    size: a.size,
    visibility: a.visibility,
    publicUrl: a.publicUrl,
    altText: typeof a.metadata?.altText === 'string' ? a.metadata.altText : null,
    version: a.updatedAt,
  };
}

export function resourceFile(r: Resource): PreviewFile {
  return { assetId: r.assetId, filename: r.name, mimeType: r.mimeType, size: r.size, visibility: r.visibility, publicUrl: r.publicUrl, version: r.updatedAt };
}

const IMAGE_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp', 'image/gif', 'image/svg+xml']);
const PDF = 'application/pdf';
const PREVIEW_MAX_BYTES = 20 * 1024 * 1024;

export const isImage = (f: PreviewFile) => IMAGE_TYPES.has(f.mimeType) && f.size <= PREVIEW_MAX_BYTES;
export const isPdf = (f: PreviewFile) => f.mimeType === PDF && f.size <= PREVIEW_MAX_BYTES;
export const hasPreview = (f: PreviewFile) => isImage(f) || isPdf(f);

/**
 * Files fetched with the Authorization header, kept for the page's life so the large view doesn't load again. The blob
 * gets the stored type, never the server's guess: a picture shows as <img>, a PDF in the browser's viewer — nothing
 * runs on our origin.
 */
const blobs = new Map<string, Promise<Blob>>();
const blobKey = (f: PreviewFile) => `${f.assetId}:${f.version ?? ''}`;
function loadBlob(f: PreviewFile): Promise<Blob> {
  const key = blobKey(f);
  let pending = blobs.get(key);
  if (!pending) {
    pending = api.assets.content(f.assetId, f.filename).then(({ blob }) => new Blob([blob], { type: f.mimeType }));
    pending.catch(() => blobs.delete(key));
    blobs.set(key, pending);
    if (blobs.size > 40) blobs.delete(blobs.keys().next().value!);
  }
  return pending;
}

export function useBlobUrl(f: PreviewFile, enabled: boolean) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!enabled) return;
    let objectUrl: string | null = null;
    let cancelled = false;
    loadBlob(f)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blobKey(f), enabled]);
  return { url, failed };
}

async function download(f: PreviewFile) {
  const blob = await loadBlob(f);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = f.filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

function PdfThumb({ file, width }: { file: PreviewFile; width: number }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const [pages, setPages] = useState<number | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let cancelled = false;
    Promise.all([loadBlob(file), import('./pdf-thumb')])
      .then(([blob, pdf]) => (cancelled || !canvas.current ? null : pdf.renderFirstPage(blob, canvas.current, width)))
      .then((n) => !cancelled && n !== null && setPages(n))
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
    };
    // The caller builds a new PreviewFile on every render; the content only changes with its key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blobKey(file), width]);
  if (failed) return <FileChipBody file={file} />;
  return (
    <span className="flex flex-col items-start gap-1">
      <canvas ref={canvas} className={cn('rounded-md border bg-white', pages === null && 'h-40 animate-pulse bg-muted/40')} style={{ width }} />
      <span className="text-xs text-muted-foreground">
        PDF{pages ? ` · ${pages === 1 ? t('1 page') : t('{n} pages', { n: pages })}` : ''} · {formatBytes(file.size)}
      </span>
    </span>
  );
}

function FileChipBody({ file }: { file: PreviewFile }) {
  return (
    <span className="inline-flex max-w-full items-center gap-2 rounded-md border bg-muted/30 px-3 py-2 text-left">
      <FileText className="size-4 shrink-0 text-muted-foreground" />
      <span className="min-w-0">
        <span className="block truncate text-sm font-medium">{file.filename}</span>
        <span className="block text-xs text-muted-foreground">
          {formatBytes(file.size)} · {file.mimeType}
        </span>
      </span>
    </span>
  );
}

/**
 * A stored file, small: a picture as a thumbnail, a PDF as its first page, anything else as a chip. A click opens it
 * large (FileViewer) with download, its public address, and public/private.
 */
export function FilePreview({ file, className }: { file: PreviewFile; className?: string }) {
  const [open, setOpen] = useState(false);
  const image = isImage(file);
  const { url, failed } = useBlobUrl(file, image);
  const alt = file.altText || file.filename;
  return (
    <div className={cn('mt-2', className)}>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-block max-w-full cursor-zoom-in rounded-md text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
        title={t('Open {name}', { name: file.filename })}
      >
        {image && !failed ? (
          url ? (
            <img src={url} alt={alt} className="max-h-48 max-w-full rounded-md border bg-muted/30 object-contain" />
          ) : (
            <span className="block h-32 w-48 animate-pulse rounded-md border bg-muted/40" />
          )
        ) : isPdf(file) ? (
          <PdfThumb file={file} width={168} />
        ) : (
          <FileChipBody file={file} />
        )}
      </button>
      {file.altText && file.altText !== file.filename && <div className="mt-1 text-xs text-muted-foreground">{t('Alt text: {text}', { text: file.altText })}</div>}
      {open && <FileViewer file={file} open={open} onOpenChange={setOpen} />}
    </div>
  );
}

/** The large view: the picture full size, a PDF in the browser's viewer; download, public address, public/private. */
export function FileViewer({ file: initial, open, onOpenChange }: { file: PreviewFile; open: boolean; onOpenChange: (open: boolean) => void }) {
  const toast = useToast();
  const [file, setFile] = useState(initial);
  const [busy, setBusy] = useState<'visibility' | 'download' | null>(null);
  const { url, failed } = useBlobUrl(file, hasPreview(file));

  // The chat keeps a copy of the file from when it was made; whether it is public now comes from the asset.
  useEffect(() => {
    api.assets
      .get(initial.assetId)
      .then((a) => setFile((f) => ({ ...f, filename: a.filename, visibility: a.visibility, publicUrl: a.publicUrl })))
      .catch(() => undefined);
  }, [initial.assetId]);

  const setVisibility = async (visibility: AssetVisibility) => {
    setBusy('visibility');
    try {
      const a = await api.assets.update(file.assetId, { visibility });
      setFile((f) => ({ ...f, visibility: a.visibility, publicUrl: a.publicUrl }));
      toast.success(visibility === 'public' ? t('Public: anyone with the link can open it') : t('Private again: the link no longer works'));
    } catch (err) {
      toast.error(t('Could not change who sees it'), errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  const isPublic = file.visibility === 'public';
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-5xl">
        <DialogHeader>
          <DialogTitle className="flex min-w-0 items-center gap-2">
            <span className="truncate">{file.filename}</span>
            <Badge tone={isPublic ? 'green' : 'muted'}>
              {isPublic ? <Globe /> : <Lock />}
              {isPublic ? t('Public') : t('Private')}
            </Badge>
          </DialogTitle>
          <DialogDescription>
            {formatBytes(file.size)} · {file.mimeType}
            {file.altText && file.altText !== file.filename ? ` · ${file.altText}` : ''}
          </DialogDescription>
        </DialogHeader>

        {hasPreview(file) && !failed ? (
          url ? (
            isImage(file) ? (
              <img src={url} alt={file.altText || file.filename} className="mx-auto max-h-[72vh] max-w-full rounded-md object-contain" />
            ) : (
              <iframe src={url} title={file.filename} className="h-[72vh] w-full rounded-md border" />
            )
          ) : (
            <div className="h-[50vh] w-full animate-pulse rounded-md bg-muted/40" />
          )
        ) : (
          <div className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">{t('No preview for this file type. Download it to open it.')}</div>
        )}

        {isPublic && file.publicUrl && (
          <div className="flex min-w-0 items-center gap-2 rounded-md border bg-muted/30 px-3 py-2">
            <Globe className="size-4 shrink-0 text-muted-foreground" />
            <code className="min-w-0 flex-1 truncate text-xs">{file.publicUrl}</code>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                void navigator.clipboard.writeText(file.publicUrl!).then(
                  () => toast.success(t('Link copied')),
                  () => toast.error(t('Could not copy the link')),
                );
              }}
            >
              <Copy /> {t('Copy link')}
            </Button>
            <Button variant="ghost" size="sm" asChild>
              <a href={file.publicUrl} target="_blank" rel="noreferrer">
                <ExternalLink /> {tc('verb', 'Open')}
              </a>
            </Button>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button variant="outline" size="sm" loading={busy === 'visibility'} onClick={() => setVisibility(isPublic ? 'private' : 'public')}>
            {busy !== 'visibility' && (isPublic ? <Lock /> : <Globe />)} {isPublic ? t('Make private') : t('Make public')}
          </Button>
          <Button
            size="sm"
            loading={busy === 'download'}
            onClick={async () => {
              setBusy('download');
              try {
                await download(file);
              } catch (err) {
                toast.error(t('Download failed'), errorMessage(err));
              } finally {
                setBusy(null);
              }
            }}
          >
            {busy !== 'download' && <Download />} {t('Download')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
